import { CommonModule } from '@angular/common';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, ElementRef, HostListener, NgZone } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { Toast, ToastPackage, ToastrService } from 'ngx-toastr';

/** Presentation adapter: all existing ToastrService calls share the feedback kit. */
@Component({
  selector: 'app-backoffice-toast',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './backoffice-toast.component.html',
  host: { '[@.disabled]': 'reduceMotion' },
  animations: [trigger('flyInOut', [
    state('inactive', style({ opacity: 0, transform: 'translateY(-6px)' })),
    state('active', style({ opacity: 1, transform: 'translateY(0)' })),
    state('removed', style({ opacity: 0, transform: 'translateY(-4px)' })),
    transition('inactive => active', animate('{{ easeTime }}ms ease-out')),
    transition('active => removed', animate('{{ easeTime }}ms ease-in')),
  ])]
})
export class BackofficeToastComponent extends Toast {
  readonly reduceMotion = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  private pointerInside = false;
  private focusInside = false;

  constructor(toastr: ToastrService, toastPackage: ToastPackage, zone: NgZone, private element: ElementRef<HTMLElement>) {
    super(toastr, toastPackage, zone);
  }

  get tone(): string {
    return this.toastPackage.toastType.replace('toast-', '');
  }

  get icon(): string {
    return { success: 'bi-check2', error: 'bi-exclamation-circle', warning: 'bi-exclamation-triangle', info: 'bi-info-circle' }[this.tone] || 'bi-info-circle';
  }

  get titleKey(): string {
    return { success: 'ac_success', error: 'ac_error', warning: 'banner.title_warning', info: 'banner.title_info' }[this.tone] || 'banner.title_info';
  }

  override stickAround(): void {
    this.pointerInside = true;
    super.stickAround();
  }

  override delayedHideToast(): void {
    this.pointerInside = false;
    if (!this.focusInside) super.delayedHideToast();
  }

  @HostListener('focusin')
  pauseForKeyboard(): void {
    this.focusInside = true;
    super.stickAround();
  }

  @HostListener('focusout', ['$event'])
  resumeAfterKeyboard(event: FocusEvent): void {
    if (event.relatedTarget && this.element.nativeElement.contains(event.relatedTarget as Node)) return;
    this.focusInside = false;
    if (!this.pointerInside) super.delayedHideToast();
  }

  dismiss(event: Event): void {
    event.stopPropagation();
    this.remove();
  }
}
